import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanSearch, Trash2 } from "lucide-react";

const fmtSize = (b) => (b >= 1e9 ? `${(b / 1e9).toFixed(2)} GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);
const fmtLen = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

export const BunnyCleanup = () => {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sel, setSel] = useState({});
  const previewEnv = typeof window !== "undefined" && !window.location.hostname.endsWith("yalola.app");

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/admin/bunny/orphans");
      setScan(data);
      const m = {};
      (data.orphans || []).forEach((o) => { if (!previewEnv || o.is_test) m[o.guid] = true; });
      setSel(m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse impossible");
    } finally { setScanning(false); }
  };

  const orphans = scan?.orphans || [];
  const selected = orphans.filter((o) => sel[o.guid]);
  const selBytes = selected.reduce((a, o) => a + (o.size || 0), 0);

  const del = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} vidéo(s) de Bunny (${fmtSize(selBytes)}) ? Elles ne sont référencées par aucun post, reel ou story de l'app.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/bunny/orphans/delete", { guids: selected.map((o) => o.guid) });
      toast.success(`${data.deleted} vidéo(s) supprimée(s) ✓`);
      await runScan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setDeleting(false); }
  };

  return (
    <div className="rounded-xl px-4 py-3 space-y-2.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="bunny-cleanup-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold">Nettoyage de la vidéothèque</div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            Repère les vidéos <strong>orphelines</strong> (tests QA, posts supprimés) qui occupent du stockage Bunny pour rien. Les vidéos utilisées par l'app ne sont jamais touchées.
          </p>
        </div>
        <Button variant="outline" className="rounded-full shrink-0" onClick={runScan} disabled={scanning} data-testid="bunny-cleanup-scan">
          <ScanSearch size={15} className="mr-1.5" />{scanning ? "Analyse..." : "Analyser la bibliothèque"}
        </Button>
      </div>
      {scan && (
        <>
          {previewEnv && (
            <div className="text-xs rounded-lg px-3 py-2 font-medium" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="bunny-cleanup-preview-warning">
              ⚠️ Environnement de prévisualisation : la base locale ne connaît pas les vidéos de production, la liste d'orphelines n'est PAS fiable ici. Seules les vidéos TEST sont sélectionnables. Utilisez cet outil depuis <strong>yalola.app</strong>.
            </div>
          )}
          <div className="text-xs font-semibold" data-testid="bunny-cleanup-summary">
            {orphans.length === 0
              ? `Aucune vidéo orpheline — bibliothèque propre ✓ (${scan.total_library} vidéo(s) au total)`
              : `${orphans.length} orpheline(s) sur ${scan.total_library} vidéos — ${fmtSize(scan.orphan_bytes)} récupérables`}
          </div>
          {orphans.length > 0 && (
            <>
              <div className="max-h-64 overflow-y-auto rounded-lg divide-y" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }} data-testid="bunny-cleanup-list">
                {orphans.map((o) => (
                  <label key={o.guid} className={`flex items-center gap-2.5 px-3 py-2 text-xs ${previewEnv && !o.is_test ? "opacity-45" : "cursor-pointer"}`} data-testid={`bunny-orphan-${o.guid}`}>
                    <input
                      type="checkbox"
                      checked={!!sel[o.guid]}
                      disabled={previewEnv && !o.is_test}
                      onChange={(e) => setSel((s) => ({ ...s, [o.guid]: e.target.checked }))}
                      className="accent-[var(--yalola-primary)]"
                      data-testid={`bunny-orphan-check-${o.guid}`}
                    />
                    <span className="flex-1 truncate font-medium">{o.title || "(sans titre)"}</span>
                    {o.is_test && <span className="chip text-[9px] shrink-0">TEST</span>}
                    <span className="shrink-0 tabular-nums" style={{ color: "var(--yalola-text-muted)" }}>{fmtLen(o.length)} · {fmtSize(o.size)} · {o.date}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <Button className="rounded-full" onClick={del} disabled={deleting || !selected.length} data-testid="bunny-cleanup-delete">
                  <Trash2 size={15} className="mr-1.5" />{deleting ? "Suppression..." : `Supprimer la sélection (${selected.length} · ${fmtSize(selBytes)})`}
                </Button>
                <button
                  type="button"
                  className="text-xs underline"
                  style={{ color: "var(--yalola-text-muted)" }}
                  onClick={() => setSel(Object.fromEntries(orphans.filter((o) => !previewEnv || o.is_test).map((o) => [o.guid, selected.length !== orphans.filter((x) => !previewEnv || x.is_test).length])))}
                  data-testid="bunny-cleanup-toggle-all"
                >
                  {selected.length === orphans.length ? "Tout décocher" : "Tout cocher"}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
